/**
 * Common animation presets for quick usage
 */

import type { AnimationConfig, CSSLength, CSSAngle } from './types';

export const presets = {
  /** Simple fade in from transparent */
  fadeIn: (delay = 0, duration = 0.5): AnimationConfig => ({
    opacityStart: 0,
    delay,
    duration,
  }),

  /** Fade in with slide from bottom */
  slideUp: (
    delay = 0,
    distance: CSSLength = '20px',
    duration = 0.5
  ): AnimationConfig => ({
    opacityStart: 0,
    translateY: distance,
    delay,
    duration,
  }),

  /** Fade in with slide from top */
  slideDown: (
    delay = 0,
    distance: CSSLength = '20px',
    duration = 0.5
  ): AnimationConfig => ({
    opacityStart: 0,
    translateY: distance.startsWith('-') ? distance : `-${distance}`,
    delay,
    duration,
  }),

  /** Fade in with slide from left */
  slideLeft: (
    delay = 0,
    distance: CSSLength = '50px',
    duration = 0.5
  ): AnimationConfig => ({
    opacityStart: 0,
    translateX: distance.startsWith('-') ? distance : `-${distance}`,
    delay,
    duration,
  }),

  /** Fade in with slide from right */
  slideRight: (
    delay = 0,
    distance: CSSLength = '50px',
    duration = 0.5
  ): AnimationConfig => ({
    opacityStart: 0,
    translateX: distance,
    delay,
    duration,
  }),

  /** Blur effect from top */
  blurUp: (
    delay = 0,
    blur: CSSLength = '10px',
    distance: CSSLength = '30px',
    duration = 0.5
  ): AnimationConfig => ({
    opacityStart: 0,
    blur,
    translateY: distance,
    delay,
    duration,
  }),

  /** Scale in effect */
  scaleIn: (delay = 0, scale = 0.9, duration = 0.5): AnimationConfig => ({
    opacityStart: 0,
    scale,
    delay,
    duration,
  }),

  /** Scale out effect */
  scaleOut: (delay = 0, scale = 1.1, duration = 0.5): AnimationConfig => ({
    opacityStart: 0,
    scale,
    delay,
    duration,
  }),

  /** Rotate and scale in */
  rotateIn: (
    delay = 0,
    rotate: CSSAngle = '10deg',
    scale = 0.95,
    duration = 0.5
  ): AnimationConfig => ({
    opacityStart: 0,
    rotate,
    scale,
    delay,
    duration,
  }),

  /** Complex blur and slide with custom timing */
  dramatic: (delay = 0, duration = 0.8): AnimationConfig => ({
    opacityStart: 0,
    blur: '15px',
    translateY: '50px',
    scale: 0.9,
    duration,
    timing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    delay,
  }),

  /** Bounce in effect */
  bounceIn: (delay = 0, duration = 0.6): AnimationConfig => ({
    opacityStart: 0,
    scale: 0.3,
    timing: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    delay,
    duration,
  }),

  /** Zoom in effect */
  zoomIn: (delay = 0, scale = 0.8, duration = 0.5): AnimationConfig => ({
    opacityStart: 0,
    scale,
    blur: '5px',
    delay,
    duration,
  }),
};
